import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log("[Auth Callback] Processing OAuth callback");
        
        // Get error and error_description from URL if present
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        if (errorParam) {
          console.error("[Auth Callback] OAuth error:", errorParam, errorDescription);
          throw new Error(errorDescription || "Error during authentication");
        }

        // Get the session to check if we're authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("[Auth Callback] Session error:", sessionError);
          throw sessionError;
        }

        if (session) {
          console.log("[Auth Callback] Session found:", session);
          
          // Check if user profile exists
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profileError) {
            console.error("[Auth Callback] Profile error:", profileError);
            throw profileError;
          }

          // If no profile exists, create one
          if (!profile) {
            console.log("[Auth Callback] Creating new profile for user");
            const { error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: session.user.id,
                email: session.user.email,
                trial_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
              });

            if (insertError) {
              console.error("[Auth Callback] Profile creation error:", insertError);
              // Don't throw here as the profile might already exist due to trigger
              console.log("[Auth Callback] Continuing despite profile creation error");
            }
          }

          toast({
            title: "Signed in successfully!",
            description: "Welcome to Strykr.ai!",
          });

          // Check if user has completed onboarding
          const { data: onboardingComplete, error: onboardingError } = await supabase
            .rpc('check_onboarding_status', {
              user_id: session.user.id
            });

          if (onboardingError) {
            console.error("[Auth Callback] Onboarding check error:", onboardingError);
            throw onboardingError;
          }

          // Redirect based on onboarding status
          if (!onboardingComplete) {
            console.log("[Auth Callback] Redirecting to onboarding");
            navigate("/onboarding");
          } else {
            console.log("[Auth Callback] Redirecting to home");
            navigate("/");
          }
        } else {
          console.log("[Auth Callback] No session found, redirecting to auth");
          navigate("/auth");
        }
      } catch (error) {
        console.error("[Auth Callback] Error:", error);
        
        let errorMessage = "Failed to sign in. Please try again.";
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        
        toast({
          variant: "destructive",
          title: "Error signing in",
          description: errorMessage,
        });
        navigate("/auth");
      }
    };

    handleCallback();
  }, [navigate, toast, searchParams]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
      <LoadingSpinner />
      <p className="text-sm text-gray-600">Completing sign in...</p>
    </div>
  );
};